import { formaterMontant } from '../../../../../packages/ui/src/format.js';
import { cheminEcran } from '../../app/ecrans.js';
import { CANAUX, FINALITES, TEXTES, type CanalChoisi, type FinaliteAffichee } from './libelles.js';

export type { CanalChoisi };

/**
 * Modèle pur des portails client. La réponse du serveur est PROJETÉE sur une liste blanche de champs : un champ que le
 * contrat ne prévoit pas (fraction de droits, nom du mandant, montant réglé, coordonnée privée) n'atteint jamais l'écran,
 * même si le serveur l'envoie par erreur (BETA-19, RG-17). Une réponse de forme inattendue lève une erreur : on n'affiche
 * jamais un portail à moitié.
 */

export type EtatEtape = 'fait' | 'courant' | 'a_venir';

export interface EtapeFrise {
  code: string;
  libelle: string;
  etat: EtatEtape;
  detail: string | null;
}

export type GenreAction = 'piece' | 'paiement' | 'consentement' | 'signature' | 'aucune';
export type EcranCible = 'M05' | 'M06' | 'M07';

export interface ActionUnique {
  genre: GenreAction;
  ecranCible: EcranCible | null;
  texte: string;
  libelleBouton: string | null;
  libelleAlternative: string | null;
  /** Adresse du bouton principal : pour une pièce, M05 sur la pièce attendue (`?piece=<id>`, RFC-DEF-PORTAIL-PIECE). */
  lien?: string | null;
  /** Pièce attendue de la personne du compte (`nextAction.requirementId`) ; null si le serveur ne la donne pas. */
  requirementId?: string | null;
}

export interface MembreFamille {
  id: string;
  nom: string;
  lien: string | null;
  vivant: boolean;
  hypothese: boolean;
  contacts: { type: string; value: string }[];
}

export type VuePaiement =
  | { genre: 'a_jour'; urlRecus: string }
  | { genre: 'reste_du'; resteDu: string; echeance: string | null; prochaine: string | null; urlRecus: string };

export interface AccueilPortail {
  titreDossier: string;
  salutation: string;
  signature: 'Cabinet MAAT';
  etapes: EtapeFrise[];
  action: ActionUnique;
  famille: MembreFamille[];
  paiement: VuePaiement;
}

export class ReponseInattendue extends Error {
  constructor(champ: string) {
    super(`Réponse du portail inattendue : ${champ}`);
    this.name = 'ReponseInattendue';
  }
}

type Objet = Record<string, unknown>;

const objet = (v: unknown, champ: string): Objet => {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) throw new ReponseInattendue(champ);
  return v as Objet;
};
const texte = (v: unknown, champ: string): string => {
  if (typeof v !== 'string' || v.length === 0) throw new ReponseInattendue(champ);
  return v;
};
const texteOuNul = (v: unknown, champ: string): string | null => (v === null || v === undefined ? null : texte(v, champ));
const liste = (v: unknown, champ: string): unknown[] => {
  if (!Array.isArray(v)) throw new ReponseInattendue(champ);
  return v;
};

const ETATS: Readonly<Record<string, EtatEtape>> = { fait: 'fait', en_cours: 'courant', a_venir: 'a_venir' };
const CIBLES: Readonly<Record<GenreAction, EcranCible | null>> = {
  piece: 'M05',
  paiement: 'M06',
  consentement: 'M07',
  signature: null,
  aucune: null,
};

/** Date locale « AAAA-MM-JJ » en « JJ/MM/AAAA », sans passer par un fuseau horaire. */
export function formaterDateLocale(dateLocale: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateLocale);
  if (!m) throw new ReponseInattendue('date');
  return `${m[3]}/${m[2]}/${m[1]}`;
}

const FORMAT_INSTANT = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'America/Guadeloupe',
});

/** Instant ISO en « JJ/MM/AAAA » à l'heure de la Guadeloupe. */
export function formaterInstant(instant: string): string {
  const d = new Date(instant);
  if (Number.isNaN(d.getTime())) throw new ReponseInattendue('instant');
  return FORMAT_INSTANT.format(d);
}

function euros(v: unknown, champ: string): string {
  const m = objet(v, champ);
  if (!Number.isSafeInteger(m['amountCents']) || m['currency'] !== 'EUR') throw new ReponseInattendue(champ);
  return formaterMontant((m['amountCents'] as number) / 100);
}

function projeterEtapes(brut: unknown): EtapeFrise[] {
  return liste(brut, 'steps').map((e, i) => {
    const o = objet(e, `steps[${i}]`);
    const etat = ETATS[String(o['state'])];
    if (!etat) throw new ReponseInattendue(`steps[${i}].state`);
    return {
      code: texte(o['code'], `steps[${i}].code`),
      libelle: texte(o['label'], `steps[${i}].label`),
      etat,
      detail: texteOuNul(o['detail'], 'detail'),
    };
  });
}

function projeterAction(brut: unknown): ActionUnique {
  const o = objet(brut, 'nextAction');
  const genre = String(o['kind']) as GenreAction;
  if (!(genre in CIBLES)) throw new ReponseInattendue('nextAction.kind');
  if (genre === 'aucune') return { genre, ecranCible: null, texte: TEXTES.rienAFaire, libelleBouton: null, libelleAlternative: null };
  if (genre === 'signature')
    return { genre, ecranCible: null, texte: texte(o['label'], 'nextAction.label'), libelleBouton: null, libelleAlternative: null };
  const ecranCible = CIBLES[genre] as EcranCible;
  // Aucune pièce n'est jamais devinée : sans identifiant du serveur, M05 s'ouvre sans pièce.
  const requirementId = genre === 'piece' ? texteOuNul(o['requirementId'], 'nextAction.requirementId') : null;
  return {
    genre,
    ecranCible,
    texte: texte(o['label'], 'nextAction.label'),
    libelleBouton: texte(o['ctaLabel'], 'nextAction.ctaLabel'),
    libelleAlternative: texteOuNul(o['alternativeLabel'], 'nextAction.alternativeLabel'),
    lien: requirementId ? `${cheminEcran(ecranCible)}?piece=${encodeURIComponent(requirementId)}` : cheminEcran(ecranCible),
    requirementId,
  };
}

function projeterFamille(brut: unknown): MembreFamille[] {
  return liste(brut, 'branch').map((p, i) => {
    const o = objet(p, `branch[${i}]`);
    const contacts = liste(o['sharedContacts'], 'sharedContacts').map((c) => {
      const co = objet(c, 'sharedContacts[]');
      return { type: texte(co['type'], 'contact.type'), value: texte(co['value'], 'contact.value') };
    });
    return {
      id: texte(o['id'], 'branch.id'),
      nom: texte(o['displayName'], 'branch.displayName'),
      lien: texteOuNul(o['relationLabel'], 'branch.relationLabel'),
      vivant: o['vitalStatus'] === 'vivant',
      hypothese: o['isHypothesis'] === true,
      contacts,
    };
  });
}

function projeterPaiement(brut: unknown): VuePaiement {
  const o = objet(brut, 'billing');
  const urlRecus = texte(o['receiptsUrl'], 'billing.receiptsUrl');
  const aJour = o['upToDate'];
  if (typeof aJour !== 'boolean') throw new ReponseInattendue('billing.upToDate');
  const reste = o['remainingDue'];
  // « À jour » et « reste dû » sont exclusifs : une réponse qui dit les deux n'est pas affichée.
  if (aJour !== (reste === null || reste === undefined)) throw new ReponseInattendue('billing.remainingDue');
  if (aJour) return { genre: 'a_jour', urlRecus };
  const prochaine =
    o['nextInstallment'] === null || o['nextInstallment'] === undefined ? null : objet(o['nextInstallment'], 'nextInstallment');
  return {
    genre: 'reste_du',
    resteDu: euros(reste, 'billing.remainingDue'),
    echeance: prochaine ? formaterDateLocale(texte(prochaine['dueAt'], 'nextInstallment.dueAt')) : null,
    prochaine: prochaine ? euros(prochaine['amount'], 'nextInstallment.amount') : null,
    urlRecus,
  };
}

export function projeterAccueil(brut: unknown): AccueilPortail {
  const o = objet(brut, 'accueil');
  return {
    titreDossier: texte(o['dossierTitle'], 'dossierTitle'),
    salutation: texte(o['greeting'], 'greeting'),
    signature: 'Cabinet MAAT',
    etapes: projeterEtapes(o['steps']),
    action: projeterAction(o['nextAction']),
    famille: projeterFamille(o['branch']),
    paiement: projeterPaiement(o['billing']),
  };
}

const pluriel = (n: number, un: string, plusieurs: string): string => (n > 1 ? plusieurs : un);

export function resumeProgression(etapes: readonly EtapeFrise[]): string {
  const total = etapes.length;
  const faites = etapes.filter((e) => e.etat === 'fait').length;
  if (faites === 0) return `Aucune étape franchie sur ${total}`;
  return `${faites} ${pluriel(faites, 'étape', 'étapes')} sur ${total} ${pluriel(faites, 'franchie', 'franchies')}`;
}

export const etapeCourante = (etapes: readonly EtapeFrise[]): EtapeFrise | null => etapes.find((e) => e.etat === 'courant') ?? null;

// --- Confidentialité (M07) ---

export interface ConfidentialiteLue {
  consents: { purpose: string; granted: boolean; capturedAt: string }[];
  preferredChannel: CanalChoisi | null;
}

export interface ChoixAffiche {
  finalite: FinaliteAffichee;
  libelle: string;
  accorde: boolean;
  le: string | null;
}

export function projeterConfidentialite(brut: unknown): ConfidentialiteLue {
  const o = objet(brut, 'confidentialite');
  const canal = o['preferredChannel'];
  if (canal !== null && canal !== undefined && !CANAUX.some((c) => c.canal === canal)) throw new ReponseInattendue('preferredChannel');
  return {
    consents: liste(o['consents'], 'consents').map((c) => {
      const co = objet(c, 'consents[]');
      return {
        purpose: texte(co['purpose'], 'purpose'),
        granted: co['granted'] === true,
        capturedAt: texte(co['capturedAt'], 'capturedAt'),
      };
    }),
    preferredChannel: (canal ?? null) as CanalChoisi | null,
  };
}

/** Les trois choix (contact, numéro, adresse) sont toujours listés ; un choix jamais exprimé vaut « non » et n'a pas de date. */
export function choixConfidentialite(brut: ConfidentialiteLue): ChoixAffiche[] {
  return FINALITES.map(({ finalite, libelle }) => {
    const lu = brut.consents.find((c) => c.purpose === finalite);
    return { finalite, libelle, accorde: lu?.granted === true, le: lu ? formaterInstant(lu.capturedAt) : null };
  });
}

export interface SaisieChoix {
  finalites: Record<FinaliteAffichee, boolean>;
  canal: CanalChoisi | null;
}

export interface CorpsModification {
  consents?: { purpose: FinaliteAffichee; granted: boolean }[];
  preferredChannel?: CanalChoisi | null;
}

/** Seul ce qui a changé est envoyé ; rien n'a changé, rien n'est envoyé (`null`). */
export function corpsModification(avant: ConfidentialiteLue, saisie: SaisieChoix): CorpsModification | null {
  const consents = choixConfidentialite(avant)
    .filter((c) => c.accorde !== saisie.finalites[c.finalite])
    .map((c) => ({ purpose: c.finalite, granted: saisie.finalites[c.finalite] }));
  const canalChange = saisie.canal !== avant.preferredChannel;
  if (consents.length === 0 && !canalChange) return null;
  return { ...(consents.length > 0 ? { consents } : {}), ...(canalChange ? { preferredChannel: saisie.canal } : {}) };
}

export const saisieDepuis = (lu: ConfidentialiteLue): SaisieChoix => ({
  finalites: Object.fromEntries(choixConfidentialite(lu).map((c) => [c.finalite, c.accorde])) as Record<FinaliteAffichee, boolean>,
  canal: lu.preferredChannel,
});
